import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import CommonHeader from "./components/CommonHeader";

const normalizeTimeForInput = (timeString) => {
  if (!timeString || typeof timeString !== "string") return "";
  // Backend typically returns HH:mm:ss
  const match = timeString.match(/^(\d{2}:\d{2})(?::\d{2})?$/);
  if (match) return match[1];
  return timeString;
};

const normalizeTimeForApi = (timeValue) => {
  if (!timeValue || typeof timeValue !== "string") return "";
  // UI may return HH:mm; backend examples use HH:mm:ss
  const match = timeValue.match(/^(\d{2}):(\d{2})$/);
  if (match) return `${match[1]}:${match[2]}:00`;
  // If already has seconds, keep as-is
  return timeValue;
};

const sortByDisplayOrder = (list) =>
  [...list].sort((a, b) => {
    const orderA = Number(a.display_order ?? 0);
    const orderB = Number(b.display_order ?? 0);
    if (orderA !== orderB) return orderA - orderB;
    return String(a.time_name || "").localeCompare(String(b.time_name || ""));
  });

const BusTimingsManagement = () => {
  const [busTimings, setBusTimings] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [editingBusTiming, setEditingBusTiming] = useState(null);
  const [formData, setFormData] = useState({
    time_name: "",
    from_time: "",
    to_time: "",
    day_flag: 0,
    display_order: 0,
    cron_job_time: "",
  });

  const fetchBusTimings = async () => {
    setLoading(true);
    try {
      const data = new URLSearchParams();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/BusTimings/getActiveBusTimings`,
        {
          method: "POST",
          headers: {
            Authorization: localStorage.getItem("token"),
          },
          body: data,
        }
      );

      const result = await response.json();
      if (result.status === 1) {
        const list = result.bus_timings || result.list || [];
        setBusTimings(sortByDisplayOrder(list));
      } else {
        toast.error(result.message || "Failed to fetch bus timings");
      }
    } catch (error) {
      console.error("Error fetching bus timings:", error);
      toast.error("Failed to fetch bus timings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBusTimings();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        name === "day_flag" || name === "display_order" ? Number(value) : value,
    }));
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingBusTiming(null);
    setFormData({
      time_name: "",
      from_time: "",
      to_time: "",
      day_flag: 0,
      display_order: 0,
      cron_job_time: "",
    });
  };

  const handleEdit = (item) => {
    setEditingBusTiming(item);
    setFormData({
      time_name: item.time_name || "",
      from_time: normalizeTimeForInput(item.from_time),
      to_time: normalizeTimeForInput(item.to_time),
      day_flag: typeof item.day_flag === "number" ? item.day_flag : Number(item.day_flag || 0),
      display_order:
        typeof item.display_order === "number"
          ? item.display_order
          : Number(item.display_order || 0),
      cron_job_time: normalizeTimeForInput(item.cron_job_time),
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.time_name.trim()) {
      toast.error("Time name is required");
      return;
    }
    if (!formData.from_time.trim() || !formData.to_time.trim()) {
      toast.error("From time and To time are required");
      return;
    }
    if (!formData.cron_job_time.trim()) {
      toast.error("Cron job time is required");
      return;
    }

    setLoading(true);
    try {
      const endpoint = editingBusTiming
        ? `${process.env.REACT_APP_API_URL}/BusTimings/updateBusTiming`
        : `${process.env.REACT_APP_API_URL}/BusTimings/createBusTiming`;

      const data = new URLSearchParams();
      if (editingBusTiming) {
        data.append("time_id", editingBusTiming.time_id);
      }

      data.append("time_name", formData.time_name);
      data.append("from_time", normalizeTimeForApi(formData.from_time));
      data.append("to_time", normalizeTimeForApi(formData.to_time));
      data.append("day_flag", String(formData.day_flag));
      data.append("display_order", String(formData.display_order));
      data.append("cron_job_time", normalizeTimeForApi(formData.cron_job_time));

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          Authorization: localStorage.getItem("token"),
        },
        body: data,
      });

      const result = await response.json();
      if (result.status === 1) {
        toast.success(result.message || "Saved successfully");
        closeModal();
        fetchBusTimings();
      } else {
        toast.error(result.message || "Operation failed");
      }
    } catch (error) {
      console.error("Error saving bus timing:", error);
      toast.error("Failed to save bus timing");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (item) => {
    if (
      window.confirm(
        `Are you sure you want to delete bus timing "${item.time_name}"?`
      ) === false
    ) {
      return;
    }

    setLoading(true);
    try {
      const data = new URLSearchParams();
      data.append("time_id", item.time_id);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/BusTimings/deleteBusTiming`,
        {
          method: "POST",
          headers: {
            Authorization: localStorage.getItem("token"),
          },
          body: data,
        }
      );

      const result = await response.json();
      if (result.status === 1) {
        toast.success(result.message || "Bus timing deleted (soft)");
        fetchBusTimings();
      } else {
        toast.error(result.message || "Failed to delete bus timing");
      }
    } catch (error) {
      console.error("Error deleting bus timing:", error);
      toast.error("Failed to delete bus timing");
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (item) => {
    if (
      window.confirm(
        `Are you sure you want to restore bus timing "${item.time_name}"?`
      ) === false
    ) {
      return;
    }

    setLoading(true);
    try {
      const data = new URLSearchParams();
      data.append("time_id", item.time_id);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/BusTimings/restoreBusTiming`,
        {
          method: "POST",
          headers: {
            Authorization: localStorage.getItem("token"),
          },
          body: data,
        }
      );

      const result = await response.json();
      if (result.status === 1) {
        toast.success(result.message || "Bus timing restored");
        fetchBusTimings();
      } else {
        toast.error(result.message || "Failed to restore bus timing");
      }
    } catch (error) {
      console.error("Error restoring bus timing:", error);
      toast.error("Failed to restore bus timing");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div class="d-flex flex-column flex-lg-row h-lg-full bg-surface-secondary">
        <div class="h-screen flex-grow-1 overflow-y-lg-auto">
          <CommonHeader />

          <main class="py-6 bg-surface-secondary">
            <div class="container-fluid">
              <div class="card shadow border-0 mb-7">
                <div class="card-header">
                  <h5 class="mb-0">Bus Timings</h5>
                </div>

                <div class="table-responsive">
                  <table class="table table-hover table-nowrap">
                    <thead class="thead-light">
                      <tr>
                        <th scope="col">Time ID</th>
                        <th scope="col">Time Name</th>
                        <th scope="col">Display Order</th>
                        <th scope="col">From</th>
                        <th scope="col">To</th>
                        <th scope="col">Day Flag</th>
                        <th scope="col">Cron Job Time</th>
                        <th scope="col">Status</th>
                        <th scope="col">Modified On</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colspan="10" class="text-center">
                            <div
                              class="spinner-border"
                              role="status"
                              aria-hidden="true"
                            ></div>
                            <span class="visually-hidden">Loading...</span>
                          </td>
                        </tr>
                      ) : busTimings.length > 0 ? (
                        busTimings.map((item) => (
                          <tr key={item.time_id}>
                            <td>{item.time_id}</td>
                            <td>{item.time_name}</td>
                            <td>{item.display_order ?? 0}</td>
                            <td>{item.from_time}</td>
                            <td>{item.to_time}</td>
                            <td>{item.day_flag}</td>
                            <td>{item.cron_job_time}</td>
                            <td>
                              {item.status == 0 ? (
                                <span
                                  class="badge badge-lg badge-dot"
                                  title="Inactive"
                                >
                                  <>
                                    <i style={{ backgroundColor: "gray" }}></i>
                                    Inactive
                                  </>
                                </span>
                              ) : (
                                <span
                                  class="badge badge-lg badge-dot"
                                  title="Active"
                                >
                                  <>
                                    <i className="bg-success"></i>
                                    Active
                                  </>
                                </span>
                              )}
                            </td>
                            <td>
                              {item.mod_on ? new Date(item.mod_on).toLocaleString() : "-"}
                            </td>
                            <td class="text-end">
                              {item.status == 0 ? (
                                <>
                                  <button
                                    onClick={() => handleEdit(item)}
                                    class="btn btn-sm btn-neutral"
                                    style={{ marginRight: "0.5rem" }}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    type="button"
                                    class="btn btn-sm btn-square btn-neutral text-success-hover"
                                    onClick={() => handleRestore(item)}
                                    title="Restore Bus Timing"
                                  >
                                    <i class="bi bi-arrow-clockwise"></i>
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    onClick={() => handleEdit(item)}
                                    class="btn btn-sm btn-neutral"
                                    style={{ marginRight: "0.5rem" }}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    type="button"
                                    class="btn btn-sm btn-square btn-neutral text-danger-hover"
                                    onClick={() => handleDelete(item)}
                                    title="Delete Bus Timing"
                                  >
                                    <i class="bi bi-trash"></i>
                                  </button>
                                </>
                              )}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colspan="10" class="text-center text-muted">
                            No bus timings found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div class="card-footer border-0 py-5">
                  <button
                    class="btn d-inline-flex btn-sm btn-primary mx-1"
                    onClick={() => {
                      setEditingBusTiming(null);
                      setFormData({
                        time_name: "",
                        from_time: "",
                        to_time: "",
                        day_flag: 0,
                        display_order: 0,
                        cron_job_time: "",
                      });
                      setShowModal(true);
                    }}
                    disabled={loading}
                  >
                    <span class="pe-2">
                      <i class="bi bi-plus"></i>
                    </span>
                    <span>Add Bus Timing</span>
                  </button>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>

      {showModal && (
        <div
          class="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div class="modal-dialog">
            <div class="modal-content">
              <div class="modal-header">
                <h5 class="modal-title">
                  {editingBusTiming ? "Edit Bus Timing" : "Add New Bus Timing"}
                </h5>
                <button type="button" class="btn-close" onClick={closeModal}></button>
              </div>

              <form onSubmit={handleSubmit}>
                <div class="modal-body">
                  <div class="mb-3">
                    <label for="time_name" class="form-label">
                      Time Name
                    </label>
                    <input
                      type="text"
                      class="form-control"
                      id="time_name"
                      name="time_name"
                      value={formData.time_name}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div class="mb-3">
                    <label for="from_time" class="form-label">
                      From Time
                    </label>
                    <input
                      type="time"
                      class="form-control"
                      id="from_time"
                      name="from_time"
                      value={normalizeTimeForInput(formData.from_time)}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div class="mb-3">
                    <label for="to_time" class="form-label">
                      To Time
                    </label>
                    <input
                      type="time"
                      class="form-control"
                      id="to_time"
                      name="to_time"
                      value={normalizeTimeForInput(formData.to_time)}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div class="mb-3">
                    <label for="day_flag" class="form-label">
                      Day Flag
                    </label>
                    <input
                      type="number"
                      class="form-control"
                      id="day_flag"
                      name="day_flag"
                      value={formData.day_flag}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div class="mb-3">
                    <label for="display_order" class="form-label">
                      Display Order
                    </label>
                    <input
                      type="number"
                      class="form-control"
                      id="display_order"
                      name="display_order"
                      min="0"
                      value={formData.display_order}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div class="mb-3">
                    <label for="cron_job_time" class="form-label">
                      Cron Job Time
                    </label>
                    <input
                      type="time"
                      class="form-control"
                      id="cron_job_time"
                      name="cron_job_time"
                      value={normalizeTimeForInput(formData.cron_job_time)}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div class="modal-footer">
                  <button
                    type="button"
                    class="btn btn-sm btn-neutral"
                    onClick={closeModal}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button type="submit" class="btn btn-sm btn-primary" disabled={loading}>
                    {editingBusTiming ? "Save Changes" : "Create"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BusTimingsManagement;

